import { useEffect } from 'react';

import { BrazeUserAttributes } from '../../../type/braze-user.interface';
import BrazeSDK from '../../common/external-library/braze-sdk';
import { formattedDate } from '../../date/formattedDate';
import useMyCouponHistoryQuery from '../react-query/useMyCouponHistoryQuery';
import useMyPointHistoryQuery from '../react-query/useMyPointHistoryQuery';
import { useMySubscriptionsQuery } from '../react-query/useMySubscriptionsQuery';
import useMeQuery from '../react-query/user/useMeQuery';

const useBraze = () => {
  const { me } = useMeQuery();
  const { refetch: fetchSubscriptions } = useMySubscriptionsQuery();
  const { refetch: fetchPoints } = useMyPointHistoryQuery({ enabled: false });
  const { refetch: fetchCoupons } = useMyCouponHistoryQuery({ enabled: false });

  const fetchUserPoints = async (): Promise<number> => {
    try {
      return (await fetchPoints()).data?.points ?? 0;
    } catch (err) {
      return 0;
    }
  };

  const fetchUserSubscription = async (): Promise<
    Pick<BrazeUserAttributes, 'subscription' | 'subscription_type'>
  > => {
    try {
      const subscriptions = (await fetchSubscriptions()).data ?? [];

      if (subscriptions.length === 0)
        return { subscription: null, subscription_type: null };

      if (subscriptions.every((subscription) => subscription.isPaused))
        return {
          subscription: 'pause',
          subscription_type: subscriptions[0].product.productType,
        };

      return {
        subscription: 'active',
        subscription_type: subscriptions[0].product.productType,
      };
    } catch (err) {
      return { subscription: null, subscription_type: null };
    }
  };

  const fetchValidCoupons = async (): Promise<
    Pick<BrazeUserAttributes, 'valid_coupon' | 'valid_coupons'>
  > => {
    try {
      const coupons = (await fetchCoupons()).data ?? [];

      if (coupons.length === 0) return { valid_coupon: 0, valid_coupons: [] };

      const canUseCoupons = coupons.filter((c) => c.canUse);

      return {
        valid_coupon: canUseCoupons.length,
        valid_coupons: canUseCoupons.map((c) => ({
          id: c.id.toString(),
          name: c.coupon.description,
          expiry: c.coupon.expiredAt
            ? formattedDate(c.coupon.expiredAt, 'yyyy-MM-dd')
            : null,
        })),
      };
    } catch (err) {
      return { valid_coupon: 0, valid_coupons: [] };
    }
  };

  /**
   * Braze SDK 초기화
   */
  useEffect(() => {
    (async () => {
      await BrazeSDK.initialize();
      await BrazeSDK.openSession();
    })();
  }, []);

  /**
   * Braze SDK 사용자 변경
   */
  useEffect(() => {
    (async () => {
      if (BrazeSDK.isInitialized && me) {
        await BrazeSDK.changeUser(me.id.toString());

        const [totalPoints, subscription, coupon] = await Promise.all([
          fetchUserPoints(),
          fetchUserSubscription(),
          fetchValidCoupons(),
        ]);

        await BrazeSDK.setUserAttributes({
          name: me.name,
          signup_date: new Date(me.createdAt),
          marketing_policy: me.marketingPolicy,
          email: me.email,
          phone: me.phone,
          delete: false,
          total_points: totalPoints,
          ...coupon,
          ...subscription,
        });
      }
    })();
  }, [me]);
};

export default useBraze;
